import "js/core/preload";
import "js/elements/elementImports";

import React from "react";

import { app } from "js/namespaces";
import log from "js/core/logger";
import { Slide } from "js/core/models/slide";
import db from "js/db";
import firebase from "js/firebase";
import { ds } from "js/core/models/dataService";

import { LoadingPage } from "js/react/components/LoadingPage";
import { withFirebaseUser } from "js/react/views/Auth/FirebaseUserContext";
import { PresentationPrivacyType } from "js/core/models/presentation";

import PlayerContext from "./PlayerContext";
import { getAnalytics, AnalyticsContextType } from "../helpers/analytics";
import { FeatureType } from "common/features";

class PlayerEditorContext extends PlayerContext {
    constructor() {
        super();

        this.state = {
            fetching: true
        };

        this.originalSelectionPresentation = null;
        this.shouldRestoreOriginalSelectionPresentation = false;
    }

    async componentDidMount() {
        const { presentation, isOpenedFromMainView, firebaseUser } = this.props;

        try {
            // Will make sure that the selection is in the right state
            if (ds.selection.presentation !== presentation) {
                this.shouldRestoreOriginalSelectionPresentation = true;
                this.originalSelectionPresentation = ds.selection.presentation;
                ds.selection.presentation = presentation;
            }

            // Note: slides won't be preloaded, the player will take care of loading them when needed
            const slides = presentation.getSips()
                .map(slideId => new Slide({ id: slideId }, { presentation, autoLoad: false }))
                .reduce((slides, slide) => ({ ...slides, [slide.id]: slide }), {});

            // Prefetching skipped slide ids (so we can correctly calculate slides count w/o having to preload all slide models)
            const skippedSlideIds = [];
            await Promise.all(
                Object.keys(slides).map(slideId =>
                    db("slides").child(slideId).child("isSkipped").once("value")
                        .then(snap => snap.val())
                        .then(isSkipped => {
                            if (isSkipped) {
                                skippedSlideIds.push(slideId);
                            }
                        })
                )
            );

            const allowPdfDownload = (
                presentation.get("allowPdfDownload") &&
                presentation.getPrivacySetting() !== PresentationPrivacyType.PRIVATE
            );

            this.contextData = {
                ...this.contextData,
                isLoggedIn: true,
                presentation,
                isPreviewFromEditor: true,
                showBranding: !app.user.features.isFeatureEnabled(FeatureType.PLAYER_SETTINGS, presentation.getWorkspaceId()),
                isCreatorOldBasicUser: app.user.isOldBasicUser(),
                slides,
                skippedSlideIds,
                onCopyDeck: false,
                allowCopyLink: false,
                allowSocialSharing: false,
                onSwitchPresentation: false,
                onEditPresentation: false,
                onCopyPresentation: false,
                onDownloadPdf: allowPdfDownload && (() => presentation.downloadExport("pdf", "local")),
                creator: {
                    displayName: firebaseUser.displayName || firebaseUser.email,
                    photoURL: firebaseUser.photoURL
                },
                isPresenterAvailable: !app.isMobileOrTablet,
                presenterUrl: "/presenter",
                presenterWindowTarget: "_blank",
                analytics: getAnalytics(isOpenedFromMainView ? AnalyticsContextType.EDITOR_MAINVIEW : AnalyticsContextType.EDITOR, false, firebaseUser, presentation.permissions.collaborator, presentation)
            };

            // Load theme
            await app.themeManager.loadTheme(presentation);

            this.setState({ fetching: false });
        } catch (err) {
            log.error(err);
        }
    }

    componentWillUnmount() {
        if (this.shouldRestoreOriginalSelectionPresentation) {
            ds.selection.presentation = this.originalSelectionPresentation;
        }
    }

    render() {
        const { fetching } = this.state;

        if (fetching) {
            return <LoadingPage />;
        }

        return this.renderChildrenWithContext();
    }
}

export default withFirebaseUser(PlayerEditorContext);
